import { useMutation, useQuery } from 'react-query';

import { FormActions, NotificationPosition } from 'coreOld/enums';
import { useHandleErrors, useInfiniteQuery } from 'coreOld/hooks';
import { GetManyResult } from 'coreOld/types';
import { actionLoadingMessage, actionMessage } from 'coreOld/utils';
import { uomsService } from 'partsOld/services';
import { IUom, IUomForm } from 'partsOld/types';

export const useGetBaseUoms = (filters?): GetManyResult<IUom> => {
  const { data, isError, isLoading } = useQuery(
    ['baseUoms', filters],
    ({ signal }) => uomsService.getManyBaseUoms(filters, signal),
    {
      keepPreviousData: true,
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count,
    isError,
    isLoading,
  };
};

export const useGetUoms = (filters?) =>
  useInfiniteQuery<IUomForm>(uomsService.getMany, 'uomsList', filters);

export const useUomsActions = () => ({
  ...useUpsertUomConversion(),
  ...useRemoveUomConversion(),
  ...useEditUom(),
  ...useExportUomConversions(),
});

const useEditUom = () => {
  const { mutateAsync } = useMutation(uomsService.editUom);

  const editUom = (data) => mutateAsync(data);

  return { editUom };
};

const useRemoveUomConversion = () => {
  const { errors } = useHandleErrors(
    actionMessage('UOM Conversion'),
    actionLoadingMessage('UOM Conversion'),
    NotificationPosition.MODAL,
    ['uomsList'],
    FormActions.REMOVE
  );

  const { mutateAsync, isLoading, isSuccess } = useMutation(uomsService.removeUomConversion, {
    ...errors,
  });

  const removeUomConversion = (data) => mutateAsync(data);

  return { removeUomConversion, isLoading, isSuccess };
};

const useUpsertUomConversion = () => {
  const { errors } = useHandleErrors(
    actionMessage('UOM Conversion'),
    actionLoadingMessage('UOM Conversion'),
    NotificationPosition.MODAL,
    ['uomsList'],
    FormActions.UPSERT
  );

  const { mutateAsync } = useMutation(uomsService.upsertUomConversion, {
    ...errors,
  });

  const upsertUomConversion = (data) => mutateAsync(data);

  return { upsertUomConversion };
};

const useExportUomConversions = () => {
  const { mutateAsync } = useMutation(uomsService.exportUomConversions);

  const exportUomConversions = (data) => mutateAsync(data);

  return { exportUomConversions };
};
